import React, { forwardRef } from "react";
import {
    Card,
    Typography,
    List,
    ListItem,
    ListItemPrefix,
    Accordion,

} from "@material-tailwind/react";
import {
    PresentationChartBarIcon,
    UserCircleIcon,
    Cog6ToothIcon,
    PowerIcon,
    ReceiptRefundIcon, PercentBadgeIcon, EnvelopeIcon, BookOpenIcon, GiftIcon, BookmarkIcon, ClipboardDocumentListIcon, UserGroupIcon
} from "@heroicons/react/24/solid";
import {
    ChevronDownIcon
} from "@heroicons/react/24/outline";
import { useTheme } from '../ThemeContext';
import { SunIcon, MoonIcon } from "@heroicons/react/24/outline";
import {Link, useLocation, useNavigate} from "react-router-dom";
import axios from "axios";
import {getCurrentUserRole} from "../../../utils/auth";


function storeScrollPosition() {
    sessionStorage.setItem('scrollPosition', window.scrollY.toString());
}

const Sidebar = forwardRef((props, ref) => {
    const [open] = React.useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const { isDark, toggleTheme } = useTheme();
    const currentRole = getCurrentUserRole();
    const isAdminUser = currentRole === "ADMIN";


    const handleLogout = async () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        };

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin"; // Default za lokalno

        await axios.post(`${API_URL}/auth/logout`, null, config);

        localStorage.removeItem('token');
        sessionStorage.setItem('scrollPosition', window.scrollY.toString());

        navigate("/login");
    };

    const getUsernameFromToken = () => {
        const token = localStorage.getItem("token");
        if (!token) return null;
        const payload = token.split(".")[1];
        const decoded = atob(payload);
        const data = JSON.parse(decoded);
        return data.sub;
    };

    const isActiveLink = (path) => {
        return location.pathname === path;
    };

    const navItemClass = (active) => (
        `mx-1 !w-[calc(100%-1.3rem)] p-2 md:p-1.5 rounded-lg border border-transparent transition-colors ${
            active
                ? "bg-cyan-50 text-cyan-950 dark:bg-panda-cyan/10 dark:border-panda-cyan/30 dark:text-panda-cyan"
                : "focus:bg-cyan-50 hover:bg-cyan-50 text-blue-gray-700 dark:text-gray-300 dark:focus:bg-dark-800 dark:hover:bg-dark-800"
        }`
    );

    const navIconClass = "h-5 w-5 md:h-4 md:w-4 text-cyan-900 dark:text-panda-cyan";

    return (
        <Card
            ref={ref}
            className="fixed top-0 left-0 h-full md:w-[15rem] p-4 shadow-none overflow-y-auto overflow-x-hidden border-none rounded-none bg-white dark:bg-dark-925 dark:border-r dark:border-dark-700"
        >


            <div className="p-2 md:p-1.5 flex items-center justify-start">
                <label htmlFor="theme-toggle" className="flex items-center cursor-pointer group">
                    <div className="relative">
                        <input
                            type="checkbox"
                            id="theme-toggle"
                            className="sr-only"
                            checked={isDark}
                            onChange={toggleTheme}
                        />

                        <div className="block border-[1px] border-gray-300 dark:border-panda-cyan/40 w-12 h-6 rounded-full bg-gradient-to-r from-amber-100 to-amber-200 dark:from-dark-850 dark:to-dark-800 transition-colors relative overflow-hidden">
                            <SunIcon className="w-3 h-3 text-amber-500 absolute left-1.5 top-1.5 opacity-60" />

                            <MoonIcon className="w-3 h-3 text-panda-cyan absolute right-1.5 top-1.5 opacity-80" />
                        </div>

                        <div className={`dot absolute left-0.5 top-0.5 bg-white dark:bg-dark-950 w-5 h-5 rounded-full transition-transform flex items-center justify-center shadow-lg ${
                            isDark ? 'transform translate-x-6' : ''
                        }`}>
                            {isDark ? (
                                <MoonIcon className="w-3 h-3 text-panda-cyan" />
                                ) : (
                                <SunIcon className="w-3 h-3 text-amber-600" />
                                )}
                        </div>
                    </div>
                </label>
            </div>

            <div className="flex items-center justify-center">
                <img src="/images/arcado-logo-circle-smaller.png" className="w-[120px] h-[120px] md:w-[100px] md:h-[100px] rounded-full dark:ring-1 dark:ring-panda-cyan/20 dark:shadow-[0_0_36px_rgba(79,209,197,0.08)]" alt="Logo"/>
            </div>


            <List>
                <Accordion
                    open={open === 1}
                    icon={
                        <ChevronDownIcon
                            strokeWidth={2.5}
                            className={`mx-auto h-4 w-4 transition-transform ${open === 1 ? "rotate-180" : ""}`}
                        />
                    }
                >


                    <div className="p-2 md:p-1.5">
                        <Typography variant="h6" color="blue-gray" className="font-normal text-sm dark:text-gray-300">
                            Welcome
                            <span className="font-bold text-cyan-900 dark:text-panda-cyan">
                                &nbsp;{getUsernameFromToken()}
                            </span>
                        </Typography>
                    </div>

                    <Link to="/inventory" onClick={storeScrollPosition}>
                        <ListItem className={navItemClass(isActiveLink("/") || isActiveLink("/inventory") || isActiveLink("/manga") || isActiveLink("/home"))}>
                            <ListItemPrefix>
                                <BookOpenIcon className={navIconClass} />
                            </ListItemPrefix>
                            <Typography color="blue-gray" className="mr-auto font-normal text-sm whitespace-nowrap dark:text-inherit">
                                Inventory
                            </Typography>
                        </ListItem>
                    </Link>

                    {isAdminUser && (
                        <>
                            <Link to="/watchlist" onClick={storeScrollPosition}>
                                <ListItem className={navItemClass(isActiveLink("/watchlist"))}>
                                    <ListItemPrefix>
                                        <BookmarkIcon className={navIconClass} />
                                    </ListItemPrefix>
                                    <Typography color="blue-gray" className="mr-auto font-normal text-sm whitespace-nowrap dark:text-inherit">
                                        Watchlist
                                    </Typography>
                                </ListItem>
                            </Link>

                            <Link to="/orders" onClick={storeScrollPosition}>
                                <ListItem className={navItemClass(isActiveLink("/orders"))}>
                                    <ListItemPrefix>
                                        <ClipboardDocumentListIcon className={navIconClass} />
                                    </ListItemPrefix>
                                    <Typography color="blue-gray" className="mr-auto font-normal text-sm whitespace-nowrap dark:text-inherit">
                                        Orders
                                    </Typography>
                                </ListItem>
                            </Link>

                            <Link to="/figures" onClick={storeScrollPosition}>
                                <ListItem className={navItemClass(isActiveLink("/figures"))}>
                                    <ListItemPrefix>
                                        <GiftIcon className={navIconClass} />
                                    </ListItemPrefix>
                                    <Typography color="blue-gray" className="mr-auto font-normal text-sm whitespace-nowrap dark:text-inherit">
                                        Pop! Figures
                                    </Typography>
                                </ListItem>
                            </Link>

                            <Link to="/refunds" onClick={storeScrollPosition}>
                                <ListItem className={navItemClass(isActiveLink("/refunds"))}>
                                    <ListItemPrefix>
                                        <ReceiptRefundIcon className={navIconClass} />
                                    </ListItemPrefix>
                                    <Typography color="blue-gray" className="mr-auto font-normal text-sm whitespace-nowrap dark:text-inherit">
                                        Refunds
                                    </Typography>
                                </ListItem>
                            </Link>

                            <Link to="/operating_expenses" onClick={storeScrollPosition}>
                                <ListItem className={navItemClass(isActiveLink("/operating_expenses"))}>
                                    <ListItemPrefix>
                                        <PercentBadgeIcon className={navIconClass} />
                                    </ListItemPrefix>
                                    <Typography color="blue-gray" className="mr-auto font-normal text-sm whitespace-nowrap dark:text-inherit">
                                        Operating Expenses
                                    </Typography>
                                </ListItem>
                            </Link>

                            <Link to="/dashboard" onClick={storeScrollPosition}>
                                <ListItem className={navItemClass(isActiveLink("/dashboard"))}>
                                    <ListItemPrefix>
                                        <PresentationChartBarIcon className={navIconClass} />
                                    </ListItemPrefix>
                                    <Typography color="blue-gray" className="mr-auto font-normal text-sm whitespace-nowrap dark:text-inherit">
                                        Dashboard
                                    </Typography>
                                </ListItem>
                            </Link>

                            <Link to="/users" onClick={storeScrollPosition}>
                                <ListItem className={navItemClass(isActiveLink("/users"))}>
                                    <ListItemPrefix>
                                        <UserGroupIcon className={navIconClass} />
                                    </ListItemPrefix>
                                    <Typography color="blue-gray" className="mr-auto font-normal text-sm whitespace-nowrap dark:text-inherit">
                                        Users
                                    </Typography>
                                </ListItem>
                            </Link>

                            <a href="https://mail.google.com/mail/u/0/#inbox" target="_blank" rel="noopener noreferrer">
                                <ListItem className={navItemClass(false)}>
                                    <ListItemPrefix>
                                        <EnvelopeIcon className={navIconClass} />
                                    </ListItemPrefix>
                                    <Typography color="blue-gray" className="mr-auto font-normal text-sm whitespace-nowrap dark:text-inherit">
                                        Email
                                    </Typography>
                                </ListItem>
                            </a>
                        </>
                    )}



                </Accordion>

                <hr className="my-2 border-blue-gray-50 dark:border-dark-700" />

                {isAdminUser && (
                    <>
                        <ListItem className={navItemClass(false)}>
                            <ListItemPrefix>
                                <UserCircleIcon className={navIconClass} />
                            </ListItemPrefix>
                            <Typography className="text-sm whitespace-nowrap dark:text-inherit">Profile</Typography>
                        </ListItem>

                        <ListItem className={navItemClass(false)}>
                            <ListItemPrefix>
                                <Cog6ToothIcon className={navIconClass} />
                            </ListItemPrefix>
                            <Typography className="text-sm whitespace-nowrap dark:text-inherit">Settings</Typography>
                        </ListItem>
                    </>
                )}

                <Link onClick={handleLogout}>
                    <ListItem className="mx-1 !w-[calc(100%-1rem)] p-2 md:p-1.5 rounded-lg border border-transparent text-red-900 transition-colors focus:bg-red-50 hover:bg-red-50 dark:text-panda-pink dark:focus:bg-panda-pink/10 dark:hover:bg-panda-pink/10">
                        <ListItemPrefix>
                            <PowerIcon className="h-5 w-5 md:h-4 md:w-4 text-red-900 dark:text-panda-pink" />
                        </ListItemPrefix>
                        <Typography className="text-sm whitespace-nowrap dark:text-inherit">Log Out</Typography>
                    </ListItem>
                </Link>

                <hr className="my-2 border-blue-gray-50 dark:border-dark-700" />

                <div className="h-8"></div>
            </List>
            <List>
                <div className="h-12"></div>
            </List>
        </Card>
    );
});

export default Sidebar;
